import { useCallback, useEffect, useState } from "react";
import { useApi, fmtDateTime } from "@/lib/api";
import { useMe } from "@/components/portal/MeContext";
import { ClockWidget } from "@/components/portal/ClockWidget";
import { PageHeader, Empty } from "@/components/portal/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const exportCsv = (rows) => {
  const head = "Employee,Job,Clock in,Clock out,Hours,Notes\n";
  const body = rows.map((r) => [r.user_name, r.project_name, r.clock_in, r.clock_out || "", r.hours, (r.notes || "").replace(/,/g, " ")].join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([head + body], { type: "text/csv" }));
  a.download = "timesheet.csv";
  a.click();
};

export default function TimeClock() {
  const api = useApi();
  const me = useMe();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api("get", "/time").then(setRows), [api]);
  useEffect(() => {
    load();
  }, [load]);

  const totals = (rows || []).reduce((acc, r) => ({ ...acc, [r.user_name]: (acc[r.user_name] || 0) + (r.hours || 0) }), {});
  return (
    <div data-testid="time-page">
      <PageHeader eyebrow="Employee tracking" title="Time clock">
        {me.role === "admin" && rows?.length > 0 && <button onClick={() => exportCsv(rows)} data-testid="export-time-btn" className="rounded-full border border-[#0E1F3F] px-4 py-2 text-sm font-semibold hover:bg-[#0E1F3F] hover:text-white">Export CSV</button>}
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-3">
        <ClockWidget onChange={load} />
        <div className="rounded-2xl border border-[#E6E2DA] bg-white p-6 lg:col-span-2">
          <p className="eyebrow mb-3">Hours by person</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(totals).map(([n, h]) => (
              <div key={n} className="rounded-lg bg-[#F2EFE9] px-4 py-3" data-testid={`hours-total-${n}`}><p className="text-sm font-semibold">{n}</p><p className="font-mono-num text-lg">{h.toFixed(2)} h</p></div>
            ))}
            {!Object.keys(totals).length && <p className="text-sm text-[#5C5850]">No hours logged yet.</p>}
          </div>
        </div>
      </div>
      <div className="mt-8 overflow-x-auto rounded-2xl border border-[#E6E2DA] bg-white">
        {rows && !rows.length ? <Empty text="No time entries yet." /> : (
          <Table data-testid="time-table">
            <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Job</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead><TableHead className="text-right">Hours</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows?.map((r) => (
                <TableRow key={r.id} data-testid={`time-row-${r.id}`}>
                  <TableCell className="font-medium">{r.user_name}</TableCell>
                  <TableCell>{r.project_name || "—"}</TableCell>
                  <TableCell>{fmtDateTime(r.clock_in)}</TableCell>
                  <TableCell>{r.clock_out ? fmtDateTime(r.clock_out) : <span className="font-semibold text-[#A67C00]">On the clock</span>}</TableCell>
                  <TableCell className="font-mono-num text-right">{r.clock_out ? r.hours.toFixed(2) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
